import React from 'react';
import {
    Box,
    BoxProps,
    Container,
    SimpleGrid,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { useResponsive } from '@/hooks/use-responsive';

interface FeatureItem {
    icon: React.ReactNode;
    title: string;
    description: string;
}

interface FeatureSectionProps extends BoxProps {
    title: string;
    subtitle: string;
    features: FeatureItem[];
    ctaButton?: React.ReactNode;
}

const FeatureSection: React.FC<FeatureSectionProps> = ({
    title,
    subtitle,
    features,
    ctaButton,
    ...props
}) => {
    const { responsive } = useResponsive();

    return (
        <Box
            {...props}
            style={{
                overflow: 'hidden',
                position: 'relative'
            }}
        >
            <Container
                size="xl"
                py={responsive({
                    xs: 24,  // Reduced padding for better above-fold visibility
                    md: 32
                })}
            >
                <Stack
                    gap={responsive({ xs: 12, md: 16 })} // Tightened spacing
                    align={responsive({
                        xs: 'start',
                        md: 'center',
                    })}
                    ta={responsive({
                        xs: 'left',
                        md: 'center',
                    })}
                >
                    <Title
                        size={responsive({
                            xs: 28,  // Slightly smaller for better space usage
                            md: 42,
                        })}
                        fw={500}
                        style={{ cursor: 'pointer' }} // Make title clickable
                        onClick={() => {
                            // Add click handler if needed
                            // console.log('Title clicked');
                        }}
                        ta={responsive({
                            xs: 'center',
                            md: 'center',
                        })}
                    >
                        {title}
                    </Title>

                    <Text
                        fz={21}
                        fw={300}
                        maw={responsive({
                            xs: '100%',
                            md: '70%',
                            lg: '60%',
                            xl: '45%',
                        })}
                        style={{ marginBottom: '8px' }} // Reduced margin
                    >
                        {subtitle}
                    </Text>

                    <SimpleGrid
                        cols={responsive({
                            xs: 1,
                            sm: 2,
                            lg: features.length > 3 ? 4 : features.length,
                        })}
                        spacing={responsive({
                            xs: 20, // Reduced spacing
                            lg: 16,
                        })}
                        style={{ width: '100%' }}
                    >
                        {features.map((feature, index) => (
                            <Stack
                                key={index}
                                gap={12} // Tightened spacing
                                align={responsive({
                                    xs: 'center',
                                    md: 'center',
                                })}
                            >
                                <Box w={32}> {/* Slightly smaller icons */}
                                    {feature.icon}
                                </Box>

                                <Title size={21} fw={500}>
                                    {feature.title}
                                </Title>

                                <Text>
                                    {feature.description}
                                </Text>
                            </Stack>
                        ))}
                    </SimpleGrid>

                    {ctaButton && (
                        <Box mt={24}> {/* Reduced margin */}
                            {ctaButton}
                        </Box>
                    )}
                </Stack>
            </Container>
        </Box>
    );
};

export default FeatureSection;